import axios from "axios";
import Order from "./Order";
import store from "../../store";

const BASE_URL = "http://localhost:32567";

class ZomatoService {
  constructor() {
    this.baseUrl = BASE_URL;
    this.apiInstance = axios.create({
      baseURL: `${this.baseUrl}/api/v1/zomato`,
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
      },
    });

    // Add request interceptor for dynamic authorization
    this.apiInstance.interceptors.request.use((config) => {
      config.headers.Authorization = `${new Date().getTime()}`;
      return config;
    });

    // Add response interceptor for error handling and retry mechanism
    this.apiInstance.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        // Skip retry for login API calls to prevent infinite loops
        if (originalRequest.url.includes("/login")) {
          throw error;
        }

        // If we haven't tried to retry yet
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          let loginAttempts = 0;
          const maxLoginAttempts = 3;

          while (loginAttempts < maxLoginAttempts) {
            try {
              await this.login();
              // After successful login, retry the original request
              return await this.apiInstance(originalRequest);
            } catch (loginError) {
              loginAttempts++;
              if (loginAttempts === maxLoginAttempts) {
                if (!store.partnersConnectionError.includes("Zomato")) {
                  store.partnersConnectionError.push("Zomato");

                  setTimeout(() => {
                    loginAttempts = 0;
                    store.partnersConnectionError =
                      store.partnersConnectionError.filter(
                        (error) => error !== "Zomato"
                      );
                  }, 10000);
                }
                throw new Error(
                  `Failed to login after ${maxLoginAttempts} attempts`
                );
              }
              // Wait for 1 second before retrying
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }
        }

        console.error("Zomato API Error:", error);
        throw error.response?.data || error;
      }
    );
  }

  async login() {
    const response = await this.apiInstance.post("/login");
    return response.data;
  }

  // Order Operations
  // example order data
  // [
  //   {
  //     "new_orders": {
  //       "count": 1,
  //       "entities": [
  //         {
  //           "tab_id": 7014697321,
  //           "updated_timestamp": "29-06-25 08:12:23"
  //         }
  //       ]
  //     }
  //   },
  //   {
  //     "preparing_orders": {
  //       "count": 0,
  //       "entities": []
  //     }
  //   },
  //   {
  //     "ready_orders": {
  //       "count": 0,
  //       "entities": []
  //     }
  //   },
  //   {
  //     "dispatched_orders": {
  //       "count": 0,
  //       "entities": []
  //     }
  //   },
  //   {
  //     "returned_orders": {
  //       "count": 0,
  //       "entities": []
  //     }
  //   }
  // ]
  async getOrders() {
    try {
      if (store.partnersConnectionError.includes("Zomato")) {
        throw new Error("Zomato API: Connection error");
      }

      const response = await this.apiInstance.get("/orders/current");

      if (!Array.isArray(response)) {
        console.warn("Zomato API: Invalid response format");
        return [];
      }

      // Define order status types we want to process
      const orderTypes = [
        "new_orders",
        "preparing_orders",
        "ready_orders",
        "dispatched_orders",
        "returned_orders",
      ];

      // Collect all order tab_ids that need details
      const orderPromises = [];

      for (const statusGroup of response) {
        // Find which order type this group represents
        const orderType = orderTypes.find((type) => statusGroup[type]);

        if (orderType && statusGroup[orderType]) {
          const { count, entities } = statusGroup[orderType];

          // Only process if there are orders in this status
          if (count > 0 && Array.isArray(entities)) {
            // Create promises for fetching details of each order
            const statusOrderPromises = entities.map(async (order) => {
              try {
                if (order.tab_id) {
                  const orderDetails = await this.getOrderDetails(order.tab_id);
                  return orderDetails;
                }
              } catch (error) {
                console.error(
                  `Zomato API: Error fetching details for order ${order.tab_id}:`,
                  error
                );
                return null;
              }
            });

            orderPromises.push(...statusOrderPromises);
          }
        }
      }
      // Wait for all order details to be fetched
      const orders = await Promise.all(orderPromises);

      // Filter out any null results from failed requests
      return orders.filter((order) => order !== null);
    } catch (error) {
      console.error("Zomato API: Error fetching orders:", error);
      return [];
    }
  }

  // exmaple order data
  // {
  //   "status": "success",
  //   "order": {
  //     "id": "7014697321",
  //     "resId": "21159415",
  //     "state": "NEW",
  //     "handoverDetails": {
  //       "time": 18,
  //       "maxTime": 23,
  //       "minTime": 13,
  //       "text": "Set food preparation time",
  //       "stepSize": 1,
  //       "isPreparationDelayAllowed": true
  //     },
  //     "creator": {
  //       "userId": "168097127",
  //       "name": "Ashish Adhav",
  //       "countryIsdCode": "91",
  //       "orderCount": 1,
  //       "orderCountDisplay": "1st order by Ashish Adhav",
  //       "address": {
  //         "id": "803132664",
  //         "address": "Keshav Kunj 3, Budhyadev Mandir Marg, Palm Beach, Sanpada, Navi Mumbai, Maharashtra (1 km, 6 mins away)",
  //         "location": {
  //           "latitude": 19.0623741,
  //           "longitude": 73.0045166
  //         },
  //         "locality": "Keshav Kunj 3, Budhyadev Mandir Marg, Palm Beach, Sanpada, Navi Mumbai, Maharashtra (1 km, 6 mins away)"
  //       },
  //       "profilePictureUrl": "https://b.zmtcdn.com/web/assets/2267aec184e096c98c46a1565a5563661664945464.png",
  //       "profileUrl": "https://www.zomato.com/users/168097127",
  //       "customerSegmentation": "STANDARD",
  //       "originalName": "Ashish Adhav"
  //     },
  //     "createdAt": "2025-06-29T14:39:50Z",
  //     "actionedAt": "2025-06-29T14:39:50Z",
  //     "paymentMethod": "PAID",
  //     "zomatoDelivered": true,
  //     "deliveryMode": "DELIVERY",
  //     "otp": "8166",
  //     "acceptExpiryTime": 300,
  //     "pickupThresholdTime": 180,
  //     "orderMessage": {
  //       "message": "VEG ONLY ORDER",
  //       "type": "SUCCESS",
  //       "icon": "e9e7",
  //       "iconType": "SUCCESS"
  //     },
  //     "paymentDetails": {
  //       "paymentType": "PAID",
  //       "paymentMethod": "PAID"
  //     },
  //     "orderMessages": [
  //       {
  //         "messageType": "ORDER",
  //         "messageTag": "header_tag",
  //         "value": {
  //           "message": "ZOMATO - DELIVERY"
  //         },
  //         "messageBgColour": "#DACBF9",
  //         "messageTextColour": "#5832A3"
  //       },
  //       {
  //         "messageType": "ORDER",
  //         "messageTag": "order_top",
  //         "value": {
  //           "message": "Send cutlery",
  //           "type": "SUCCESS",
  //           "icon": "e9c2"
  //         }
  //       }
  //     ],
  //     "alert": {
  //       "alertType": "RING",
  //       "toOpen": true,
  //       "eventType": "ORDER_NEW"
  //     },
  //     "meta": {
  //       "actionExpiryTime": "2025-06-29T14:44:50Z",
  //       "actionExpiryDuration": 300
  //     },
  //     "nextStates": [
  //       "MERCHANT_REJECTED",
  //       "UNDER_REVIEW",
  //       "PREPARING"
  //     ],
  //     "updatedAt": "2025-06-29T14:39:50Z",
  //     "cartDetails": {
  //       "items": {
  //         "dishes": [
  //           {
  //             "id": "1",
  //             "name": "Tandlachi Bhakri",
  //             "referenceType": "DRT_VARIANT",
  //             "referenceId": "864953420",
  //             "quantity": 2,
  //             "unitCost": 39,
  //             "totalCost": 78,
  //             "subCategoryId": "66856939",
  //             "calculations": [
  //               {
  //                 "id": "1",
  //                 "appliedOnType": "DCAOT_DISH",
  //                 "appliedOnAmount": 78,
  //                 "name": "SGST",
  //                 "entityType": "DCT_SOURCE_TAX",
  //                 "entityId": "3",
  //                 "isPercentage": true,
  //                 "value": 2.5,
  //                 "amount": 1.95000005,
  //                 "calcAppliedOnAmount": 78,
  //                 "appliedPercentageValue": 2.5,
  //                 "calcAmount": 1.95
  //               },
  //               {
  //                 "id": "2",
  //                 "appliedOnType": "DCAOT_DISH",
  //                 "appliedOnAmount": 78,
  //                 "name": "SGST",
  //                 "entityType": "DCT_TAX",
  //                 "entityId": "3",
  //                 "isPercentage": true,
  //                 "value": 2.5,
  //                 "amount": 1.95000005,
  //                 "calcAppliedOnAmount": 78,
  //                 "appliedPercentageValue": 2.5,
  //                 "calcAmount": 1.95
  //               },
  //               {
  //                 "id": "3",
  //                 "appliedOnType": "DCAOT_DISH",
  //                 "appliedOnAmount": 78,
  //                 "name": "CGST",
  //                 "entityType": "DCT_SOURCE_TAX",
  //                 "entityId": "4",
  //                 "isPercentage": true,
  //                 "value": 2.5,
  //                 "amount": 1.95000005,
  //                 "calcAppliedOnAmount": 78,
  //                 "appliedPercentageValue": 2.5,
  //                 "calcAmount": 1.95
  //               },
  //               {
  //                 "id": "4",
  //                 "appliedOnType": "DCAOT_DISH",
  //                 "appliedOnAmount": 78,
  //                 "name": "CGST",
  //                 "entityType": "DCT_TAX",
  //                 "entityId": "4",
  //                 "isPercentage": true,
  //                 "value": 2.5,
  //                 "amount": 1.95000005,
  //                 "calcAppliedOnAmount": 78,
  //                 "appliedPercentageValue": 2.5,
  //                 "calcAmount": 1.95
  //               }
  //             ],
  //             "metadata": {
  //               "tags": [
  //                 "veg",
  //                 "services",
  //                 "dt-bread",
  //                 "dt-tandlachi bhakri",
  //                 "dt-bhakri",
  //                 "dt-indian bread",
  //                 "dt-lunch",
  //                 "dt-dinner"
  //               ],
  //               "catalogueId": "639233216",
  //               "refVei": "6902511205335047620",
  //               "catalogueVei": "2749988654207117025"
  //             },
  //             "displayCost": "₹78",
  //             "dishUnitCost": 39,
  //             "dishTotalCost": 78
  //           }
  //         ]
  //       },
  //       "charges": [
  //         {
  //           "amountDetails": {
  //             "id": "13",
  //             "itemName": "Taxes",
  //             "quantity": 1,
  //             "type": "tax",
  //             "unitCost": 3.9000001,
  //             "displayCost": "₹0",
  //             "amountTotalCost": 3.9,
  //             "amountUnitCost": 3.9
  //           },
  //           "amountBreakup": {
  //             "title": "Taxes",
  //             "body": {
  //               "keyValues": [
  //                 {
  //                   "key": {
  //                     "message": "Net tax on order (paid by customer)"
  //                   },
  //                   "value": {
  //                     "message": "₹3.90"
  //                   }
  //                 },
  //                 {
  //                   "key": {
  //                     "message": "GST on restaurant services under section 9(5)"
  //                   },
  //                   "value": {
  //                     "message": "-₹3.90"
  //                   }
  //                 }
  //               ],
  //               "footer": {
  //                 "key": {
  //                   "message": "Net tax transferrable to restaurant"
  //                 },
  //                 "value": {
  //                   "message": "₹0.00"
  //                 }
  //               }
  //             }
  //           }
  //         }
  //       ],
  //       "subtotal": {
  //         "amountDetails": {
  //           "id": "17",
  //           "itemName": "Item total",
  //           "totalCost": 78,
  //           "type": "subtotal2",
  //           "displayCost": "₹78",
  //           "amountTotalCost": 78
  //         }
  //       },
  //       "total": {
  //         "amountDetails": {
  //           "id": "60457403511",
  //           "itemName": "Total Bill",
  //           "totalCost": 78,
  //           "type": "total_merchant",
  //           "unitCost": 78,
  //           "displayCost": "₹78",
  //           "amountTotalCost": 78,
  //           "amountUnitCost": 78
  //         }
  //       }
  //     },
  //     "supportingRiderDetails": [
  //       {
  //         "riderStatus": "REQUESTED",
  //         "title": {
  //           "message": "5 riders nearby, assigning one soon",
  //           "type": "INFO",
  //           "icon": "e99c",
  //           "iconType": "INFO"
  //         }
  //       }
  //     ],
  //     "displayId": "7321",
  //     "shouldShowKptWidget": true,
  //     "show_kot_checklist": false,
  //     "help_chat_config": {
  //       "res_id": "21159415",
  //       "subject": "Order related issues",
  //       "issue_type": "Order related issues",
  //       "issue_sub_type": "Live chat for order support",
  //       "order_id": "7014697321",
  //       "issue_id": 800
  //     },
  //     "live_order_support_deeplink": "zomato-merchant://unified-support/builder?flowType=builder&issue_id=800&order_id=7014697321&res_id=21159415&service_type=Live_Order_Support&subject=Order related issues&issue_type=Order related issues&issue_sub_type=Live chat for order support",
  //     "billPrintData": {
  //       "qrCode": {
  //         "scanText": "7014697321",
  //         "description": "Zomato delivery partner will scan to pickup the order"
  //       }
  //     },
  //     "kotPrintData": {
  //       "qrCode": {
  //         "scanText": "7014697321",
  //         "description": "Zomato delivery partner will scan to pickup the order"
  //       }
  //     }
  //   }
  // }

  async getOrderDetails(orderId) {
    const orderData = await this.apiInstance.get(
      `/order/details?order_id=${orderId}`
    );
    const { order, status } = orderData;
    if (status === "success") {
      return new Order(order, "zomato", this);
    } else {
      console.error("Zomato API: Error fetching order details:", orderData);
      return null;
    }
  }

  // These methods are now internal and called by Order class
  async acceptOrder(orderId, deliveryTime = "30") {
    return this.apiInstance.post(
      `/orders/accept_order?order_id=${orderId}&delivery_time=${deliveryTime}`
    );
  }

  async markOrderReady(orderId) {
    return this.apiInstance.post(`/orders/mark_ready?order_id=${orderId}`);
  }

  async rejectOrder(orderId, restaurantId) {
    return this.apiInstance.post(
      `/orders/reject?restaurant_id=${restaurantId}&order_id=${orderId}`
    );
  }

  // Item Operations
  async getItems() {
    return this.apiInstance.get("/items");
  }

  async markItemInStock(itemId) {
    return this.apiInstance.post(`/items/in_stock?item_id=${itemId}`);
  }

  async markItemOutOfStock(itemId) {
    return this.apiInstance.post(`/items/out_of_stock?item_id=${itemId}`);
  }

  // Category Operations
  async markCategoryInStock(categoryId) {
    return this.apiInstance.post(
      `/category/in_stock?category_id=${categoryId}`
    );
  }

  // Outlet Operations
  async closeOutlet() {
    return this.apiInstance.post("/close_outlet");
  }

  async reopenOutlet() {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);

    const params = {
      date: String(now.getDate()).padStart(2, "0"),
      month: String(now.getMonth() + 1).padStart(2, "0"),
      year: String(now.getFullYear()),
      hour: String(now.getHours()).padStart(2, "0"),
      min: String(now.getMinutes()).padStart(2, "0"),
      sec: String(now.getSeconds()).padStart(2, "0"),
    };

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return this.apiInstance.post(`/close_outlet?${queryString}`);
  }
}

export const zomatoService = new ZomatoService();
